import { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react'

export type ObserverKey =
  | 'blocked-places'
  | 'affected-students'
  | 'enrollment-comments'
  | 'dispatch-enrollments'
  | 'dispatch-staffs'
  | 'enrollment-events'
  | 'staff-requests'
  | 'affected-staffs'
  | 'update-affected-staff-data'
  | 'affected-staff-send-contract'
  | 'affected-staff-view'
  | 'ol-affected-staff-table'
  // | 'assign-host-family-to-staff'
  | 'trip-folder-young-passengers'
  | 'diffuse-trip-booking'
  | 'ol-center-data'
  | 'send-activties-to-lec'
  | 'open-activities'
  | 'validate-activities'
  | 'ol-center-activity-crud'
  | 'ol-center-activity-table'
  | 'ol-teaching-hall-table'
  | 'ol-teaching-hall-crud'
  | 'center-local-organizers-costs-table'
  | 'center-host-family-costs-table'
  | 'center-tefl-costs-table'
  | 'center-teaching-hall-costs-table'
  | 'center-total-costs'
  | 'refetch-center-manager-and-fac-terrain'
  | 'center-staffs'
  | 'center-global-view'
  | 'center-update-global-view'
  | 'center-cost'
  | 'enrollment-profile'
  | 'enrollment-client-personal-space'
  | 'update-enrollment-profile-client-personal-space'
  | 'enrollment-global-view'
  | 'update-payment-status'
  | 'enrollment-proposition-comparison'
  | 'enrollment-proposition-manager'
  | 'click-affected-staff-card'
  | 'update-navbar-affected-staffs'
  | 'update-staff-progress-bar'
  | 'refresh-staff-request'
  | 'enrollment-form-update'
  | 'enrollment-health-information'
  | 'trip-booking-payments'
  | 'trip-booking-update'
  | 'enrollment-payment-record'
  | 'enrollment-profile-monitoring-payment-record'
  | 'enrollment-overpayment'
  | 'enrollment-proposition-summary'
  | 'enrollment-status'

type Observer<T> = {
  key?: ObserverKey
  update?: (data?: T) => Promise<unknown> | void
}

type ObserverContextType<T extends object> = {
  addObserver: (key: ObserverKey, update: (data?: T) => void) => void
  removeObserver: (key: ObserverKey) => void
  notify: (keys: ObserverKey[], data?: T) => Promise<void> | void
}

// Context to hold the state
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ObserverContext = createContext<ObserverContextType<any>>({
  addObserver: () => {},
  removeObserver: () => {},
  notify: () => {}
})

// Provider component
export const ObserverProvider = <T extends object>({ children }: PropsWithChildren) => {
  const [observers, setObservers] = useState<Observer<T>[]>([])

  const addObserver = (key: ObserverKey, update: (data?: T) => void) => {
    // Check if the observer already exists
    if (observers.some((observer) => observer.key === key)) return
    setObservers((prev) => [...prev, { key, update }])
  }

  const removeObserver = (key: ObserverKey) => {
    setObservers((prev) => prev.filter((observer) => observer.key !== key))
  }

  const notify = async (keys: ObserverKey[], data?: T) => {
    for (const observer of observers)
      if (observer.key && keys.includes(observer.key)) await observer.update?.(data)
  }

  return (
    <ObserverContext.Provider value={{ addObserver, removeObserver, notify }}>
      {children}
    </ObserverContext.Provider>
  )
}

// Custom hook to use context
export const useObserver = <T extends object>(observer?: Observer<T>) => {
  const context = useContext(ObserverContext)

  useEffect(() => {
    if (observer?.key) context.addObserver(observer.key, (data: T) => observer.update?.(data))
    return () => {
      if (observer?.key) context.removeObserver(observer.key)
    }
  }, [])
  return context
}
